import { useState, useEffect } from 'react';
import { db, storage } from '../../firebase/config';
import { collection, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Upload, X } from 'lucide-react';

const MagazineForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        date: new Date().toLocaleDateString('tr-TR'),
        description: '',
        coverImage: '',
        pdfUrl: '',
        pages: [] // Array of image URLs
    });

    useEffect(() => {
        if (isEditMode) {
            const fetchMag = async () => {
                setLoading(true);
                const docRef = doc(db, "magazines", id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setFormData(docSnap.data());
                } else {
                    alert("Dergi bulunamadı!");
                    navigate('/admin/magazines');
                }
                setLoading(false);
            };
            fetchMag();
        }
    }, [id, isEditMode, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const storageRef = ref(storage, `magazines/covers/${Date.now()}_${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            const url = await getDownloadURL(snapshot.ref);
            setFormData(prev => ({ ...prev, coverImage: url }));
        } catch (error) {
            console.error("Resim yükleme hatası:", error);
            alert("Resim yüklenirken hata oluştu.");
        }
    };

    const handlePageUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setLoading(true);
        try {
            const uploadedUrls = await Promise.all(
                files.map(async (file) => {
                    const storageRef = ref(storage, `magazines/pages/${Date.now()}_${file.name}`);
                    const snapshot = await uploadBytes(storageRef, file);
                    return getDownloadURL(snapshot.ref);
                })
            );

            setFormData(prev => ({
                ...prev,
                pages: [...(prev.pages || []), ...uploadedUrls]
            }));

        } catch (error) {
            console.error("Sayfa yükleme hatası:", error);
            alert("Sayfalar yüklenirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const dataToSave = {
                ...formData,
                updatedAt: new Date().toISOString()
            };

            if (isEditMode) {
                await updateDoc(doc(db, "magazines", id), dataToSave);
            } else {
                await addDoc(collection(db, "magazines"), {
                    ...dataToSave,
                    createdAt: new Date().toISOString()
                });
            }
            navigate('/admin/magazines');
        } catch (error) {
            console.error("Kayıt hatası:", error);
            alert("Kaydedilirken bir hata oluştu: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading && isEditMode && !formData.title) return <div className="text-white">Yükleniyor...</div>;

    return (
        <div className="max-w-2xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
                <button
                    onClick={() => navigate('/admin/magazines')}
                    className="p-2 hover:bg-white/10 rounded-full text-gray-400 hover:text-white transition-colors"
                >
                    <ArrowLeft size={24} />
                </button>
                <h1 className="text-2xl font-bold text-white">
                    {isEditMode ? 'Dergiyi Düzenle' : 'Yeni Sayı Ekle'}
                </h1>
            </div>

            <form onSubmit={handleSubmit} className="bg-gray-800 rounded-xl p-6 border border-gray-700 space-y-6">

                {/* Title */}
                <div>
                    <label className="block text-gray-400 text-sm font-medium mb-2">Başlık / Sayı Adı</label>
                    <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                        placeholder="Örn: TURET Dergi - Sayı 5"
                    />
                </div>

                {/* Date */}
                <div>
                    <label className="block text-gray-400 text-sm font-medium mb-2">Yayın Tarihi</label>
                    <input
                        type="text"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        required
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                        placeholder="DD.MM.YYYY"
                    />
                </div>

                {/* PDF Link */}
                <div>
                    <label className="block text-gray-400 text-sm font-medium mb-2">PDF Linki (Drive vb.)</label>
                    <input
                        type="url"
                        name="pdfUrl"
                        value={formData.pdfUrl}
                        onChange={handleChange}
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                        placeholder="https://..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Google Drive linki veya başka bir dosya linki yapıştırabilirsiniz.</p>
                </div>

                {/* Description */}
                <div>
                    <label className="block text-gray-400 text-sm font-medium mb-2">Kısa Açıklama</label>
                    <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors h-24 resize-none"
                        placeholder="Bu sayı hakkında kısa bilgi..."
                    />
                </div>

                {/* Cover Image Upload */}
                <div>
                    <label className="block text-gray-400 text-sm font-medium mb-2">Kapak Resmi</label>
                    <div className="flex flex-col md:flex-row items-start gap-6">
                        {formData.coverImage && (
                            <div className="relative w-full md:w-32 h-64 md:h-44 rounded-lg overflow-hidden border border-gray-600 group shadow-lg">
                                <img src={formData.coverImage} alt="Preview" className="w-full h-full object-cover" />
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, coverImage: '' }))}
                                    className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-red-500"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                        )}
                        <div className="flex-1">
                            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-600 border-dashed rounded-lg cursor-pointer hover:bg-gray-700/50 hover:border-cyan-500 transition-all">
                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                    <Upload className="w-8 h-8 mb-2 text-gray-400" />
                                    <p className="text-sm text-gray-400"><span className="font-semibold">Kapak görseli yükle</span></p>
                                    <p className="text-xs text-gray-500">Dikey format (A4 gibi) önerilir</p>
                                </div>
                                <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                            </label>
                        </div>
                    </div>
                </div>

                {/* Pages Upload Section */}
                <div>
                    <label className="block text-gray-400 text-sm font-medium mb-2">Dergi Sayfaları (Sırayla Yükleyiniz)</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-4">
                        {/* Existing Pages List */}
                        {formData.pages && formData.pages.map((pageUrl, idx) => (
                            <div key={idx} className="relative aspect-[3/4] rounded-lg overflow-hidden border border-gray-600 group">
                                <span className="absolute top-1 left-1 bg-black/60 text-white text-[10px] px-1.5 rounded z-10">{idx + 1}</span>
                                <img src={pageUrl} alt={`Page ${idx + 1}`} className="w-full h-full object-cover" />
                                <button
                                    type="button"
                                    onClick={() => {
                                        const newPages = formData.pages.filter((_, i) => i !== idx);
                                        setFormData(prev => ({ ...prev, pages: newPages }));
                                    }}
                                    className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-red-500"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        ))}

                        {/* Upload Button */}
                        <label className="flex flex-col items-center justify-center aspect-[3/4] border-2 border-gray-600 border-dashed rounded-lg cursor-pointer hover:bg-gray-700/50 hover:border-cyan-500 transition-all">
                            <div className="flex flex-col items-center justify-center p-2 text-center">
                                {loading ? (
                                    <span className="text-xs text-cyan-400 animate-pulse">Yükleniyor...</span>
                                ) : (
                                    <>
                                        <Upload className="w-6 h-6 mb-1 text-gray-400" />
                                        <p className="text-xs text-gray-400">Sayfa Ekle</p>
                                        <p className="text-[9px] text-gray-500">Çoklu Seçim</p>
                                    </>
                                )}
                            </div>
                            <input
                                type="file"
                                className="hidden"
                                accept="image/*"
                                multiple
                                onChange={handlePageUpload}
                                disabled={loading}
                            />
                        </label>
                    </div>
                    <p className="text-xs text-gray-500">İpucu: Sayfalar yüklendikleri sırayla görüntülenecektir. Yanlış sıradaysa silip tekrar yükleyebilirsiniz.</p>
                </div>

                <div className="flex justify-end pt-4">
                    <button
                        type="submit"
                        disabled={loading}
                        className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-3 rounded-lg font-bold flex items-center gap-2 transition-all disabled:opacity-50"
                    >
                        <Save size={20} />
                        {loading ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default MagazineForm;
